/* eslint-disable */
import {
  CREATE_CLIENT_CREDENTIAL,
  CREATE_CLIENT_CREDENTIAL_SUCCESS,
  CREATE_CLIENT_CREDENTIAL_FAILURE,
  GET_CLIENT_CREDENTIALS,
  GET_CLIENT_CREDENTIALS_SUCCESS,
  GET_CLIENT_CREDENTIALS_FAILURE,
  DELETE_CLIENT_CREDENTIAL,
  DELETE_CLIENT_CREDENTIAL_SUCCESS,
  DELETE_CLIENT_CREDENTIAL_FAILURE,
  ROTATE_CLIENT_SECRET,
  ROTATE_CLIENT_SECRET_SUCCESS,
  ROTATE_CLIENT_SECRET_FAILURE,
  CLEAR_SECRET_DATA,
} from './types';
import { ThunkAction } from 'redux-thunk';
import { AppState } from 'store';
import {
  ClientCredentialsApiService,
  ICreateClientCredentialRequest,
  ICreateClientCredentialResponse,
  IClientCredentialsPaginated,
  IRotateClientSecretRequest,
  IRotateClientSecretResponse,
} from '../../core/clientCredentialsApiService';
import { handleServerError } from '../../globals/utils/handleServerError';
import { PARSAction } from 'core/models';

// —————————————————————————————————————————————
// Create Client Credential
// —————————————————————————————————————————————
export const createClientCredentialAction = (
  payload: ICreateClientCredentialRequest,
): PARSAction<ICreateClientCredentialRequest, typeof CREATE_CLIENT_CREDENTIAL> => ({
  type: CREATE_CLIENT_CREDENTIAL,
  payload,
});

export const createClientCredentialSuccessAction = (
  payload: ICreateClientCredentialResponse,
): PARSAction<ICreateClientCredentialResponse, typeof CREATE_CLIENT_CREDENTIAL_SUCCESS> => ({
  type: CREATE_CLIENT_CREDENTIAL_SUCCESS,
  payload,
});

export const createClientCredentialFailureAction = (
  payload: Error,
): PARSAction<Error, typeof CREATE_CLIENT_CREDENTIAL_FAILURE> => ({
  type: CREATE_CLIENT_CREDENTIAL_FAILURE,
  payload,
});

export const createClientCredential =
  (item: ICreateClientCredentialRequest): ThunkAction<Promise<void>, AppState, null, PARSAction> =>
  async (dispatch) => {
    dispatch(createClientCredentialAction(item));
    try {
      const service = new ClientCredentialsApiService();
      const result = await service.createAsync(item);
      dispatch(createClientCredentialSuccessAction(result));
    } catch (error) {
      handleServerError({ error, thunkName: 'createClientCredential' });
      dispatch(createClientCredentialFailureAction(error as Error));
    }
  };

// —————————————————————————————————————————————
// Fetch Client Credentials (List)
// —————————————————————————————————————————————
export const fetchClientCredentialsAction = (): PARSAction<undefined, typeof GET_CLIENT_CREDENTIALS> => ({
  type: GET_CLIENT_CREDENTIALS,
});

export const fetchClientCredentialsSuccessAction = (
  payload: IClientCredentialsPaginated,
): PARSAction<IClientCredentialsPaginated, typeof GET_CLIENT_CREDENTIALS_SUCCESS> => ({
  type: GET_CLIENT_CREDENTIALS_SUCCESS,
  payload,
});

export const fetchClientCredentialsFailureAction = (
  payload: Error,
): PARSAction<Error, typeof GET_CLIENT_CREDENTIALS_FAILURE> => ({
  type: GET_CLIENT_CREDENTIALS_FAILURE,
  payload,
});

export const fetchClientCredentials =
  (
    status: number,
    pageNumber: number,
    pageSize: number,
  ): ThunkAction<Promise<void>, AppState, null, PARSAction> =>
  async (dispatch) => {
    dispatch(fetchClientCredentialsAction());
    try {
      const service = new ClientCredentialsApiService();
      const result: IClientCredentialsPaginated = await service.getItems(status, pageNumber, pageSize);
      dispatch(fetchClientCredentialsSuccessAction(result));
    } catch (error) {
      handleServerError({ error, thunkName: 'fetchClientCredentials' });
      dispatch(fetchClientCredentialsFailureAction(error as Error));
    }
  };

// —————————————————————————————————————————————
// Delete Client Credential
// —————————————————————————————————————————————
export const deleteClientCredentialAction = (): PARSAction<undefined, typeof DELETE_CLIENT_CREDENTIAL> => ({
  type: DELETE_CLIENT_CREDENTIAL,
});

export const deleteClientCredentialSuccessAction = (
  payload: string,
): PARSAction<string, typeof DELETE_CLIENT_CREDENTIAL_SUCCESS> => ({
  type: DELETE_CLIENT_CREDENTIAL_SUCCESS,
  payload,
});

export const deleteClientCredentialFailureAction = (
  payload: Error,
): PARSAction<Error, typeof DELETE_CLIENT_CREDENTIAL_FAILURE> => ({
  type: DELETE_CLIENT_CREDENTIAL_FAILURE,
  payload,
});

export const deleteClientCredential =
  (id: string): ThunkAction<Promise<void>, AppState, null, PARSAction> =>
  async (dispatch) => {
    dispatch(deleteClientCredentialAction());
    try {
      const service = new ClientCredentialsApiService();
      await service.deleteItemAsync(id);
      dispatch(deleteClientCredentialSuccessAction(id));
    } catch (error) {
      handleServerError({ error, thunkName: 'deleteClientCredential' });
      dispatch(deleteClientCredentialFailureAction(error as Error));
    }
  };

// —————————————————————————————————————————————
// Rotate Client Secret
// —————————————————————————————————————————————
export const rotateClientSecretAction = (
  payload: IRotateClientSecretRequest,
): PARSAction<IRotateClientSecretRequest, typeof ROTATE_CLIENT_SECRET> => ({
  type: ROTATE_CLIENT_SECRET,
  payload,
});

export const rotateClientSecretSuccessAction = (
  payload: IRotateClientSecretResponse,
): PARSAction<IRotateClientSecretResponse, typeof ROTATE_CLIENT_SECRET_SUCCESS> => ({
  type: ROTATE_CLIENT_SECRET_SUCCESS,
  payload,
});

export const rotateClientSecretFailureAction = (
  payload: Error,
): PARSAction<Error, typeof ROTATE_CLIENT_SECRET_FAILURE> => ({
  type: ROTATE_CLIENT_SECRET_FAILURE,
  payload,
});

export const rotateClientSecret =
  (request: IRotateClientSecretRequest): ThunkAction<Promise<void>, AppState, null, PARSAction> =>
  async (dispatch) => {
    dispatch(rotateClientSecretAction(request));
    try {
      const service = new ClientCredentialsApiService();
      const result = await service.rotateClientSecretAsync(request);
      dispatch(rotateClientSecretSuccessAction(result));
    } catch (error) {
      handleServerError({ error, thunkName: 'rotateClientSecret' });
      dispatch(rotateClientSecretFailureAction(error as Error));
    }
  };

// —————————————————————————————————————————————
// Clear Secret Data
// —————————————————————————————————————————————
export const clearSecretDataAction = (): PARSAction<undefined, typeof CLEAR_SECRET_DATA> => ({
  type: CLEAR_SECRET_DATA,
});

export const clearSecretData = (): ThunkAction<void, AppState, null, PARSAction> => (dispatch) => {
  dispatch(clearSecretDataAction());
};
